import { getToken } from './tokenService';

/**
 * Service de telechargement - URLs directes avec ?token=xxx
 * Le middleware backend convertit ?token=xxx en Authorization: Bearer xxx
 * Plus besoin de fetch+blob : window.open(url?token=xxx) fonctionne directement
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

function tokenParam() {
  const t = getToken();
  return t ? `token=${encodeURIComponent(t)}` : '';
}

function buildUrl(path, extraParams = '') {
  const base = path.startsWith('http') ? path : `${API}${path}`;
  const sep = base.includes('?') ? '&' : '?';
  const params = [tokenParam(), extraParams].filter(Boolean).join('&');
  return params ? `${base}${sep}${params}` : base;
}

// ============================================================================
// VISUALISATION (ouvre dans un nouvel onglet)
// ============================================================================

export async function viewDocument(doc) {
  if (!doc) return;
  const url = resolveViewUrl(doc);
  if (url) {
    window.open(url, '_blank');
    return;
  }
  // Fallback S3
  const docId = doc.ged_id || doc.s3_id || doc.id;
  if (docId) return viewDocS3(docId);
  throw new Error('Document non disponible pour la visualisation');
}

// ============================================================================
// TELECHARGEMENT (lance le telechargement)
// ============================================================================

export async function downloadDocument(doc) {
  if (!doc) return;
  const url = resolveDownloadUrl(doc);
  if (url) {
    await triggerDownloadSafe(url, doc.nom || doc.original_filename || doc.filename || 'document.pdf');
    return;
  }
  // Fallback S3
  const docId = doc.ged_id || doc.s3_id || doc.id;
  if (docId) return downloadDocS3(docId);
  throw new Error('Document non disponible pour le telechargement');
}

// ============================================================================
// ROUTEUR D'URL
// ============================================================================

function resolveViewUrl(doc) {
  return resolveUrl(doc, 'view');
}

function resolveDownloadUrl(doc) {
  return resolveUrl(doc, 'download');
}

function resolveUrl(doc, mode) {
  const viewParam = mode === 'view' ? 'mode=view' : '';
  // 1. Attestation FTPE
  if (doc.type === 'attestation_ftpe' && doc.adhesion_id) {
    return buildUrl(`/api/ftpe-adhesion/attestation/${encodeURIComponent(doc.adhesion_id)}/download`, viewParam);
  }
  // 2. Certificat O'SCOP
  if (doc.type === 'certificat_adhesion' && doc.url) {
    return buildUrl(doc.url, viewParam);
  }
  // 3. Recu FTPE
  if (doc.type === 'recu_paiement_ftpe' && doc.adhesion_id) {
    return buildUrl(`/api/ftpe-adhesion/recu/${encodeURIComponent(doc.adhesion_id)}/download`, viewParam);
  }
  // 4. Recu ESS
  if (doc.type === 'recu_ess' && doc.recu_id) {
    return buildUrl(`/api/recus-ess/${encodeURIComponent(doc.recu_id)}/download`, viewParam);
  }
  // 5. Admin upload
  if (doc.source === 'admin_upload' && doc.url) {
    return buildUrl(doc.url, viewParam);
  }
  // 6. RCDS / souscription
  if (doc.souscription_id) {
    const docType = doc.type === 'conditions_generales' ? 'pack2' : doc.type === 'devis_ess' ? 'devis' : 'pack1';
    return buildUrl(`/api/souscriptions/${encodeURIComponent(doc.souscription_id)}/download-${docType}`, viewParam);
  }
  // 6bis. Proposition ESS BATICO générée depuis le simulateur (sans souscription
  // associée) : doc.url pointe directement vers /api/souscriptions/simulateur/
  // download-devis/<devis_id> et l'endpoint accepte ?token=... → on l'utilise
  // tel quel au lieu de tomber dans la branche legacy /api/ged/download qui
  // transformerait l'URL en path GED inexistant.
  const directDevisUrl = doc.url || doc.download_url || '';
  if (directDevisUrl.includes('/simulateur/download-devis/')
      || (doc.type === 'devis_ess_genere')
      || (doc.source === 'ged_simulateur' && directDevisUrl.includes('download-devis'))) {
    // Construire l'URL absolue préfixée par REACT_APP_BACKEND_URL si relative.
    const path = directDevisUrl.startsWith('http')
      ? directDevisUrl
      : (directDevisUrl.startsWith('/') ? directDevisUrl : `/${directDevisUrl}`);
    return buildUrl(path, viewParam);
  }
  // 7. URL directe / Legacy
  const rawUrl = doc.url || doc.download_url || doc.file_url || doc.path;
  if (rawUrl) {
    // Préférer le téléchargement par doc_id (route robuste avec lookup DB)
    // plutôt que /api/ged/download?path=… (sensible à la disparition du fichier).
    if (doc.id) {
      return buildUrl(`/api/ged/documents/${encodeURIComponent(doc.id)}/download`, viewParam);
    }
    let cleanPath = rawUrl;
    if (rawUrl.startsWith('/api/')) cleanPath = rawUrl.replace('/api/', '');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    if (cleanPath.startsWith('uploads/')) cleanPath = cleanPath.substring(8);
    const params = [`path=${encodeURIComponent(cleanPath)}`, viewParam].filter(Boolean).join('&');
    return buildUrl(`/api/ged/download`, params);
  }
  // 8. Fallback ultime par doc_id même sans url
  if (doc.id) {
    return buildUrl(`/api/ged/documents/${encodeURIComponent(doc.id)}/download`, viewParam);
  }
  return null;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Ajoute l'extension `.pdf` au filename si nécessaire (évite que le navigateur
 * sauvegarde une réponse JSON d'erreur avec une extension `.json`).
 */
function ensurePdfExtension(filename) {
  if (!filename) return 'document.pdf';
  const f = String(filename).trim();
  // Si déjà une extension reconnue, on garde
  if (/\.(pdf|png|jpg|jpeg|gif|webp|docx|xlsx|csv|zip)$/i.test(f)) return f;
  return `${f}.pdf`;
}

/**
 * Télécharge un fichier via fetch + Blob (au lieu de `<a download>` direct),
 * pour pouvoir détecter et afficher proprement les erreurs HTTP (404, 401, etc.)
 * au lieu de sauvegarder le corps JSON d'erreur avec une extension `.json`.
 */
async function triggerDownloadSafe(url, filename) {
  const safeName = ensurePdfExtension(filename);
  try {
    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(url, { headers, cache: 'no-cache' });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) detail = j.detail;
      } catch {/* ignore */}
      throw new Error(detail);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    triggerDownload(blobUrl, safeName);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
  } catch (err) {
    // Si le fetch préalable échoue (CORS, anti-bot, etc.), on retombe sur
    // l'ancien comportement <a download> avec une extension forcée.
    // eslint-disable-next-line no-console
    console.warn('[downloadService] fetch préalable échoué, fallback <a> direct:', err?.message);
    triggerDownload(url, safeName);
    throw err;
  }
}

function triggerDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => document.body.removeChild(link), 500);
}

// S3/R2
export async function downloadDocS3(docId) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}/api/ged/s3/documents/${docId}/signed-url`, { headers });
  if (!res.ok) throw new Error(`S3 signed-url failed (${res.status})`);
  const data = await res.json();
  if (!data?.url) throw new Error("signed-url response missing 'url'");
  window.location.assign(data.url);
}

export async function viewDocS3(docId) {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API}/api/ged/s3/documents/${docId}/signed-url`, { method: "POST", headers });
  if (!res.ok) throw new Error(`signed-url failed (${res.status})`);
  const data = await res.json();
  if (!data?.url) throw new Error("signed-url response missing 'url'");
  window.open(data.url, '_blank');
}

// ============================================================================
// FONCTIONS SPECIALISEES (compat avec les imports existants)
// ============================================================================

export const downloadAttestationFTPE = (adhesionId) => {
  if (!adhesionId) return;
  const url = buildUrl(`/api/ftpe-adhesion/attestation/${encodeURIComponent(adhesionId)}/download`);
  triggerDownload(url, 'Attestation_FTPE.pdf');
};

export const downloadRecuFTPE = (adhesionId) => {
  if (!adhesionId) return;
  const url = buildUrl(`/api/ftpe-adhesion/recu/${encodeURIComponent(adhesionId)}/download`);
  triggerDownload(url, 'Recu_FTPE.pdf');
};

export const downloadRecuESS = (recuId) => {
  if (!recuId) return;
  const url = buildUrl(`/api/recus-ess/${encodeURIComponent(recuId)}/download`);
  triggerDownload(url, 'Recu_ESS.pdf');
};

export const downloadDocumentRCDS = (souscriptionId, docType = 'pack1') => {
  if (!souscriptionId) return;
  const url = buildUrl(`/api/souscriptions/${encodeURIComponent(souscriptionId)}/download-${docType}`);
  triggerDownload(url, `Document_RCDS_${docType}.pdf`);
};

export const downloadDomiumDoc = (reference, docType) => {
  if (!reference) return;
  const url = buildUrl(`/api/domium/documents/${encodeURIComponent(reference)}/${docType}`);
  triggerDownload(url, `Document_Domium_${docType}.pdf`);
};

export const downloadByPath = (path, filename = null) => {
  if (!path) return;
  let cleanPath = path;
  if (path.startsWith('/api/')) cleanPath = path.replace('/api/', '');
  if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
  if (cleanPath.startsWith('uploads/')) cleanPath = cleanPath.substring(8);
  const url = buildUrl(`/api/ged/download`, `path=${encodeURIComponent(cleanPath)}`);
  triggerDownload(url, filename || 'document');
};

export const downloadById = (docId) => {
  if (!docId) return;
  downloadDocS3(docId).catch(console.error);
};

export const downloadFromUrl = (url, filename) => {
  if (!url) return;
  const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
  triggerDownload(buildUrl(fullUrl), filename || 'document');
};

export const downloadDoc = downloadDocS3;
export const downloadViaSigned = downloadDocS3;

export { getToken };

export default {
  downloadDocS3, downloadDocument, downloadDoc, downloadViaSigned, downloadById,
  downloadAttestationFTPE, downloadRecuFTPE, downloadRecuESS, downloadDocumentRCDS, downloadDomiumDoc,
  downloadByPath, downloadFromUrl, viewDocument, viewDocS3, getToken,
};
